/**
 * Appstle bundle free gift helper.
 *
 * Adds one Clear Glass Mandrill Bottle after a customer adds a product from the
 * Appstle bundle page. The gift is marked with line item properties so we can
 * identify only the copy this script added.
 */
(function () {
  const FREE_GIFT_VARIANT_ID = '50802950865192';
  const DISCOUNT_CODE = 'BUNDLEFREEBOTTLE';
  const DISCOUNT_STORAGE_KEY = 'bundle_free_gift_discount_code';
  const BUNDLE_PATH_PATTERN = /\/apps\/bundles\//;
  const RECENT_BUNDLE_ADD_KEY = 'mandrill_recent_bundle_add_at';
  const DISCOUNT_CLEAR_NAVIGATION_KEY = 'mandrill_bundle_discount_cleared_at';
  const RECENT_BUNDLE_ADD_WINDOW = 30 * 1000;
  const DISCOUNT_NAVIGATION_WINDOW = 15 * 1000;
  const CHECK_DEBOUNCE_MS = 450;

  let isProcessing = false;
  let queuedCheck = null;

  function cartUrl(endpoint) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${endpoint}`.replace(/\/{2,}/g, '/');
  }

  function isBundlePage() {
    return BUNDLE_PATH_PATTERN.test(window.location.pathname);
  }

  function markRecentBundleAdd() {
    try {
      sessionStorage.setItem(RECENT_BUNDLE_ADD_KEY, String(Date.now()));
    } catch (error) {
      // Storage may be unavailable in some privacy modes. The cart-property
      // detection still handles carts that include Appstle bundle markers.
    }
  }

  function hasRecentBundleAdd() {
    try {
      const timestamp = Number(sessionStorage.getItem(RECENT_BUNDLE_ADD_KEY));
      return Number.isFinite(timestamp) && Date.now() - timestamp < RECENT_BUNDLE_ADD_WINDOW;
    } catch (error) {
      return false;
    }
  }

  function clearRecentBundleAdd() {
    try {
      sessionStorage.removeItem(RECENT_BUNDLE_ADD_KEY);
    } catch (error) {
      // No-op.
    }
  }

  function stringifyForMatch(value) {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value).toLowerCase();
    }

    try {
      return JSON.stringify(value).toLowerCase();
    } catch (error) {
      return '';
    }
  }

  function hasBundleText(value) {
    const text = stringifyForMatch(value);
    return text.includes('appstle') || (text.includes('bundle') && !text.includes(DISCOUNT_CODE.toLowerCase()));
  }

  function hasBundleGiftDiscountText(value) {
    return stringifyForMatch(value).includes(DISCOUNT_CODE.toLowerCase());
  }

  function isGiftItem(item) {
    return item?.variant_id?.toString() === FREE_GIFT_VARIANT_ID;
  }

  function isAutoGiftItem(item) {
    return (
      isGiftItem(item) &&
      item.properties &&
      (item.properties._appstle_bundle_gift === 'true' || item.properties._auto_free_gift === 'true')
    );
  }

  function itemHasBundleSignal(item) {
    if (!item || isGiftItem(item)) return false;

    if (hasBundleText(item.properties)) return true;
    if (hasBundleText(item.discounts)) return true;
    if (hasBundleText(item.line_level_discount_allocations)) return true;

    return false;
  }

  function cartHasBundleSignal(cart) {
    if (!cart) return false;
    if (hasBundleText(cart.attributes)) return true;

    return Array.isArray(cart.items) && cart.items.some(itemHasBundleSignal);
  }

  function cartHasBundleGiftDiscount(cart) {
    if (!cart) return false;
    if (hasBundleGiftDiscountText(cart.cart_level_discount_applications)) return true;

    return (
      Array.isArray(cart.items) &&
      cart.items.some((item) => hasBundleGiftDiscountText(item.discounts) || hasBundleGiftDiscountText(item.line_level_discount_allocations))
    );
  }

  function hasGiftInCart(cart) {
    return Array.isArray(cart.items) && cart.items.some(isGiftItem);
  }

  function findAutoGift(cart) {
    return Array.isArray(cart.items) ? cart.items.find(isAutoGiftItem) : null;
  }

  function hasManualGift(cart) {
    return Array.isArray(cart.items) && cart.items.some((item) => isGiftItem(item) && !isAutoGiftItem(item));
  }

  async function getCart() {
    const response = await fetch(cartUrl('cart.js'), {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  async function addGift() {
    const response = await fetch(cartUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(FREE_GIFT_VARIANT_ID),
            quantity: 1,
            properties: {
              _appstle_bundle_gift: 'true',
              _auto_free_gift: 'true',
            },
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Unable to add bundle gift: ${response.status}`);
    }

    return response.json();
  }

  async function removeGift(item) {
    const response = await fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: item.key,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error(`Unable to remove bundle gift: ${response.status}`);
    }

    return response.json();
  }

  function requestContainsGiftVariant(input, init) {
    const body = init?.body || (input instanceof Request ? input.body : null);

    if (!body) return false;

    if (body instanceof FormData || body instanceof URLSearchParams) {
      return body.get('id')?.toString() === FREE_GIFT_VARIANT_ID;
    }

    if (typeof body === 'string') {
      if (body.includes(`id=${FREE_GIFT_VARIANT_ID}`)) return true;

      try {
        const data = JSON.parse(body);
        return (
          data?.id?.toString() === FREE_GIFT_VARIANT_ID ||
          (Array.isArray(data?.items) && data.items.some((item) => item?.id?.toString() === FREE_GIFT_VARIANT_ID))
        );
      } catch (error) {
        return false;
      }
    }

    return false;
  }

  async function clearDiscountBeforeManualGiftAdd() {
    if (isBundlePage() || hasRecentBundleAdd()) return;

    clearDiscountCode();

    try {
      await fetch('/discount/none', {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html,application/xhtml+xml',
        },
      });
    } catch (error) {
      // Shopify does not expose a formal cart-discount clearing API. The later
      // cart sync will still redirect through /discount/none if this request is
      // ignored by the storefront.
    }
  }

  function clearDiscountCode() {
    try {
      const storedCode = localStorage.getItem(DISCOUNT_STORAGE_KEY);
      if (storedCode === DISCOUNT_CODE) {
        localStorage.removeItem(DISCOUNT_STORAGE_KEY);
      }
    } catch (error) {
      // No-op.
    }

    document.cookie = 'discount_code=;path=/;max-age=0';
    resetCheckoutDiscountTargets();
    setCheckoutDiscountInputs(false);
  }

  function hasPersistedBundleDiscount() {
    try {
      return localStorage.getItem(DISCOUNT_STORAGE_KEY) === DISCOUNT_CODE;
    } catch (error) {
      return false;
    }
  }

  function shouldNavigateToClearDiscount() {
    try {
      const timestamp = Number(sessionStorage.getItem(DISCOUNT_CLEAR_NAVIGATION_KEY));
      return !Number.isFinite(timestamp) || Date.now() - timestamp > DISCOUNT_NAVIGATION_WINDOW;
    } catch (error) {
      return true;
    }
  }

  function markDiscountClearNavigation() {
    try {
      sessionStorage.setItem(DISCOUNT_CLEAR_NAVIGATION_KEY, String(Date.now()));
    } catch (error) {
      // No-op.
    }
  }

  function clearPersistedDiscountFromShopify() {
    clearDiscountCode();

    if (!shouldNavigateToClearDiscount()) return true;

    markDiscountClearNavigation();
    window.location.href = '/discount/none?redirect=/cart';
    return true;
  }

  function updateCheckoutDiscountTargets() {
    resetCheckoutDiscountTargets();
  }

  function resetCheckoutDiscountTargets() {
    document.querySelectorAll('#cart, #CartDrawer-Form').forEach((form) => {
      form.action = '/cart';
    });

    document.querySelectorAll('a[href^="/checkout?discount="]').forEach((link) => {
      link.href = '/checkout';
    });
  }

  function setCheckoutDiscountInputs(enabled) {
    const checkoutDiscountUrl = `/checkout?discount=${encodeURIComponent(DISCOUNT_CODE)}`;

    document.querySelectorAll('#cart, #CartDrawer-Form').forEach((form) => {
      let input = form.querySelector('input[name="discount"][data-bundle-free-gift-discount]');

      if (!enabled) {
        input?.remove();
        form.action = '/cart';
        return;
      }

      form.action = checkoutDiscountUrl;

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'discount';
        input.dataset.bundleFreeGiftDiscount = 'true';
        form.appendChild(input);
      }

      input.value = DISCOUNT_CODE;
    });
  }

  function installCheckoutInterceptor() {
    document.addEventListener(
      'click',
      async (event) => {
        const checkoutButton = event.target.closest('button[name="checkout"], #checkout, #CartDrawer-Checkout, a[href="/checkout"]');
        if (!checkoutButton) return;

        event.preventDefault();
        event.stopPropagation();
        await routeCheckout(checkoutButton);
      },
      true
    );

    document.addEventListener(
      'submit',
      async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.querySelector('[name="checkout"]') && form.id !== 'cart' && form.id !== 'CartDrawer-Form') return;

        event.preventDefault();
        event.stopPropagation();
        await routeCheckout(form);
      },
      true
    );
  }

  async function routeCheckout(sourceElement) {
    try {
      const cart = await getCart();
      const hasAutoGift = Array.isArray(cart.items) && cart.items.some(isAutoGiftItem);
      const nonGiftItems = Array.isArray(cart.items) ? cart.items.filter((item) => !isAutoGiftItem(item)) : [];

      if (hasAutoGift && nonGiftItems.length > 0) {
        try {
          localStorage.setItem(DISCOUNT_STORAGE_KEY, DISCOUNT_CODE);
        } catch (error) {
          // No-op.
        }

        document.cookie = `discount_code=${encodeURIComponent(DISCOUNT_CODE)};path=/;max-age=86400`;
        setCheckoutDiscountInputs(true);
        window.location.href = `/discount/${encodeURIComponent(DISCOUNT_CODE)}?redirect=/checkout`;
        return;
      }
    } catch (error) {
      console.error('[BundleFreeGift] Checkout eligibility check failed:', error);
    }

    clearDiscountCode();

    if (sourceElement instanceof HTMLFormElement) {
      sourceElement.submit();
      return;
    }

    window.location.href = '/checkout';
  }

  async function refreshCartUi() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    if (window.FreeProductManager && typeof window.FreeProductManager.refreshCartDisplay === 'function') {
      await window.FreeProductManager.refreshCartDisplay();
    }
  }

  async function syncBundleGift() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await getCart();
      const nonGiftItems = Array.isArray(cart.items) ? cart.items.filter((item) => !isAutoGiftItem(item)) : [];
      const hasBundle = cartHasBundleSignal(cart) || hasRecentBundleAdd();
      const giftInCart = hasGiftInCart(cart);

      if (hasBundle && !giftInCart && nonGiftItems.length > 0) {
        await addGift();
        clearRecentBundleAdd();
        await refreshCartUi();
        return;
      }

      const autoGift = findAutoGift(cart);
      const manualGift = hasManualGift(cart);

      if (!hasBundle && (manualGift || hasPersistedBundleDiscount() || cartHasBundleGiftDiscount(cart))) {
        clearPersistedDiscountFromShopify();
        return;
      }

      setCheckoutDiscountInputs(Boolean(autoGift && nonGiftItems.length > 0));

      if (!hasBundle && autoGift && nonGiftItems.length === 0) {
        await removeGift(autoGift);
        clearDiscountCode();
        clearRecentBundleAdd();
        await refreshCartUi();
      }
    } catch (error) {
      console.error('[BundleFreeGift] Sync failed:', error);
    } finally {
      isProcessing = false;
    }
  }

  function scheduleSync(delay = CHECK_DEBOUNCE_MS) {
    window.clearTimeout(queuedCheck);
    queuedCheck = window.setTimeout(syncBundleGift, delay);
  }

  function installFetchObserver() {
    if (window.__mandrillBundleGiftFetchObserverInstalled) return;
    window.__mandrillBundleGiftFetchObserverInstalled = true;

    const nativeFetch = window.fetch;
    window.fetch = async function observedFetch(input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';

      if (/\/cart\/add\.js/.test(url) && requestContainsGiftVariant(input, init)) {
        await clearDiscountBeforeManualGiftAdd();
      }

      const response = await nativeFetch.apply(this, arguments);

      if (/\/cart\/add\.js|\/cart\/change\.js|\/cart\/update\.js/.test(url)) {
        if (isBundlePage() && /\/cart\/add\.js/.test(url)) {
          markRecentBundleAdd();
        }

        scheduleSync();
      }

      return response;
    };
  }

  function installXhrObserver() {
    if (window.__mandrillBundleGiftXhrObserverInstalled || !window.XMLHttpRequest) return;
    window.__mandrillBundleGiftXhrObserverInstalled = true;

    const nativeOpen = window.XMLHttpRequest.prototype.open;
    const nativeSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function observedOpen(method, url) {
      this.__mandrillBundleGiftUrl = url;
      return nativeOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function observedSend() {
      this.addEventListener('loadend', () => {
        const url = stringifyForMatch(this.__mandrillBundleGiftUrl);

        if (/\/cart\/add\.js|\/cart\/change\.js|\/cart\/update\.js/.test(url)) {
          if (isBundlePage() && /\/cart\/add\.js/.test(url)) {
            markRecentBundleAdd();
          }

          scheduleSync();
        }
      });

      return nativeSend.apply(this, arguments);
    };
  }

  installFetchObserver();
  installXhrObserver();
  installCheckoutInterceptor();

  document.addEventListener('DOMContentLoaded', () => {
    updateCheckoutDiscountTargets();
    scheduleSync(900);
  });
  document.addEventListener('cart:item-added', () => scheduleSync());
  document.addEventListener('cart:refresh', () => {
    updateCheckoutDiscountTargets();
    scheduleSync(700);
  });
  document.addEventListener('cart:drawer-updated', () => {
    updateCheckoutDiscountTargets();
    scheduleSync(700);
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) scheduleSync();
  });

  window.BundleFreeGiftManager = {
    sync: syncBundleGift,
    variantId: FREE_GIFT_VARIANT_ID,
    discountCode: DISCOUNT_CODE,
  };
})();
